import _extends from "@babel/runtime/helpers/extends";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _isEmpty from "lodash/isEmpty";
var _excluded = ["ref"],
  _excluded2 = ["analytics"];
import "regenerator-runtime/runtime.js";
import React, { useContext, useCallback, useMemo } from 'react';
import * as NerdWalletReactAnalytics from '@nerdwallet/analytics/react/impression';
import NerdWalletAnalytics from '@nerdwallet/analytics';
import { Tab } from '@nerdwallet/react-tabs';
import { MORE_DETAILS } from '../lib/constants';
import { buildAnalyticsEntityName } from '../lib/utils';
var concatPropKey = function concatPropKey(elementName, label) {
  if (label && elementName) {
    return buildAnalyticsEntityName("".concat(elementName, "_").concat(label));
  }
  return elementName;
};

/**
 * Must consume and forward both an `onClick` and `active` prop which will be cloned
 * into each component instance by the overarching `<Tabs />` parent.
 */

var TabImpression = function TabImpression(_ref) {
  var _analytics$payload$se, _analytics$payload, _analytics$payload$se2, _analytics$payload2, _analytics$onTabClick, _analytics$onClickPro;
  var active = _ref.active,
    analytics = _ref.analytics,
    children = _ref.children,
    label = _ref.label,
    onClick = _ref.onClick,
    onTabClick = _ref.onTabClick;
  var entityName = analytics === null || analytics === void 0 ? void 0 : analytics.entityName;
  var eventType = analytics === null || analytics === void 0 ? void 0 : analytics.eventType;
  var sectionName = (_analytics$payload$se = analytics === null || analytics === void 0 ? void 0 : (_analytics$payload = analytics.payload) === null || _analytics$payload === void 0 ? void 0 : _analytics$payload.section_name) !== null && _analytics$payload$se !== void 0 ? _analytics$payload$se : analytics === null || analytics === void 0 ? void 0 : analytics.sectionName;
  var sectionPosition = (_analytics$payload$se2 = analytics === null || analytics === void 0 ? void 0 : (_analytics$payload2 = analytics.payload) === null || _analytics$payload2 === void 0 ? void 0 : _analytics$payload2.section_position) !== null && _analytics$payload$se2 !== void 0 ? _analytics$payload$se2 : analytics === null || analytics === void 0 ? void 0 : analytics.sectionPosition;
  var tabClickEntityName = analytics === null || analytics === void 0 ? void 0 : (_analytics$onTabClick = analytics.onTabClickProps) === null || _analytics$onTabClick === void 0 ? void 0 : _analytics$onTabClick.entityName;
  var clickEntityName = analytics === null || analytics === void 0 ? void 0 : (_analytics$onClickPro = analytics.onClickProps) === null || _analytics$onClickPro === void 0 ? void 0 : _analytics$onClickPro.entityName;
  var impressionContext = useContext(NerdWalletReactAnalytics.ImpressionContext);
  var parentImpressionId = impressionContext === null || impressionContext === void 0 ? void 0 : impressionContext.impressionId;
  var impressionConfig = useMemo(function () {
    return {
      eventType: eventType || 'element_impression',
      entityName: buildAnalyticsEntityName(entityName || MORE_DETAILS),
      payload: {
        parent_impression_id: parentImpressionId,
        section_name: sectionName !== null && sectionName !== void 0 ? sectionName : MORE_DETAILS,
        section_position: sectionPosition !== null && sectionPosition !== void 0 ? sectionPosition : 0
      }
    };
  }, [entityName, eventType, sectionName, sectionPosition, parentImpressionId]);
  var _NerdWalletReactAnaly = NerdWalletReactAnalytics.useImpression(impressionConfig),
    ref = _NerdWalletReactAnaly.ref,
    impression = _objectWithoutProperties(_NerdWalletReactAnaly, _excluded);
  var impressionId = impression === null || impression === void 0 ? void 0 : impression.impressionId;
  var internalOnTabClick = useCallback(function () {
    var defaultName = concatPropKey("".concat(MORE_DETAILS, "_tab_click"), label);
    var name = buildAnalyticsEntityName(tabClickEntityName) || defaultName;
    NerdWalletAnalytics.track('element_interacted', {
      action: 'expand',
      entity_name: name,
      impression_id: impressionId,
      interaction_type: 'press'
    });
    onTabClick === null || onTabClick === void 0 ? void 0 : onTabClick();
  }, [label, tabClickEntityName, onTabClick, impressionId]);
  var internalOnClick = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
    var defaultName, name;
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            defaultName = concatPropKey("".concat(MORE_DETAILS, "_click"), label);
            name = buildAnalyticsEntityName(clickEntityName) || defaultName;
            _context.next = 4;
            return NerdWalletAnalytics.track('element_interacted', {
              action: 'expand',
              entity_name: name,
              impression_id: impressionId,
              interaction_type: 'press'
            });
          case 4:
            _context.next = 6;
            return NerdWalletAnalytics.flush();
          case 6:
            onClick === null || onClick === void 0 ? void 0 : onClick();
          case 7:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [label, clickEntityName, onClick, impressionId]);
  return /*#__PURE__*/React.createElement(Tab, {
    active: active,
    label: label,
    onClick: internalOnClick,
    onTabClick: internalOnTabClick,
    ref: ref
  }, /*#__PURE__*/React.createElement(NerdWalletReactAnalytics.ImpressionContext.Provider, {
    value: impression
  }, children));
};

/**
 * Render with impression if analytics configuration is present.
 */
var TabWithMaybeImpression = function TabWithMaybeImpression(_ref3) {
  var analytics = _ref3.analytics,
    props = _objectWithoutProperties(_ref3, _excluded2);
  if (!props.children) {
    return null;
  }
  return _isEmpty(analytics) ? /*#__PURE__*/React.createElement(Tab, props) : /*#__PURE__*/React.createElement(TabImpression, _extends({
    analytics: analytics
  }, props));
};
if (process.env.NODE_ENV !== 'production') {
  /* eslint-disable global-require, @typescript-eslint/no-var-requires */
  var PropTypes = require('prop-types');
  var _require = require('../prop-types'),
    AnalyticsPropType = _require.AnalyticsPropType;
  TabImpression.propTypes = {
    analytics: AnalyticsPropType,
    children: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    onTabClick: PropTypes.func
  };
  TabWithMaybeImpression.propTypes = TabImpression.propTypes;
  /* eslint-enable global-require, @typescript-eslint/no-var-requires */
}

export default TabWithMaybeImpression;